// mirror of auware/.../frontend/src/components/layout/AppShell.tsx (see frontend/CLAUDE.md mirror policy).
//
// Buyist adaptations:
//  - Nav items: Tickets (everyone) + Tasks + Activity + Inbox
//    (agents only), in that order (Greg, 2026-08-08). Dashboard
//    and Reports were pulled for launch on 2026-05-18 (placeholders
//    only; Greg's call). The root path `/` redirects to `/tickets`.
//    System Admin and Webhooks were removed from the sidebar
//    earlier the same day; System Admin lives in the avatar menu,
//    Webhooks lives inside System Settings.
//  - Brand swapped from `/logo.webp` + "Auware" to the BuyistBrand
//    component / `/buyist-logo.png`.
//  - Auware-only widgets dropped (Intercom, BillingBanner, BugReport,
//    StoreSelector, etc.).

import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";

import { EmailSendModeBanner } from "@/components/layout/EmailSendModeBanner";
import { useAdminInboxCount } from "@/hooks/useAdminInbox";
import { clearSearchNavState } from "@/hooks/useClearSearchSignal";
import { useUnassignedTicketCount } from "@/hooks/useUnassignedTicketCount";
import { useAuthStore } from "@/stores/auth";

// ---------------------------------------------------------------------------
// Navigation items per SPEC.md Section 4
// ---------------------------------------------------------------------------

interface NavItem {
  label: string;
  to: string;
  icon: string;
  /** Restrict to agents / system_admins. */
  agentsOnly?: boolean;
  /** Restrict to system_admins. */
  adminsOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  {
    label: "Tickets",
    to: "/tickets",
    icon: "fa-duotone fa-thin fa-ticket",
  },
  {
    // Between Tickets and Activity (Greg, 2026-08-08: "Tickets,
    // Tasks, Activity, Inbox"). No count badge - his call: the list
    // will always be long, so the number carries no signal.
    label: "Tasks",
    to: "/tasks",
    icon: "fa-duotone fa-thin fa-list-check",
    agentsOnly: true,
  },
  {
    // Running feed of recently touched tickets (Greg, 2026-07-30).
    // Between Tickets and Inbox at his request.
    label: "Activity",
    to: "/activity",
    icon: "fa-duotone fa-thin fa-wave-pulse",
    agentsOnly: true,
  },
  {
    label: "Inbox",
    to: "/inbox",
    icon: "fa-duotone fa-thin fa-inbox-in",
    agentsOnly: true,
  },
  // System Admin lives on the profile menu (top-right avatar),
  // Webhooks lives inside System Settings, and Dashboard / Reports
  // were pulled for launch on 2026-05-18 (placeholders only). The
  // root path `/` redirects to `/tickets` in App.tsx.
];

// ---------------------------------------------------------------------------
// Sidebar link helper
// ---------------------------------------------------------------------------

function SidebarLink({
  item,
  collapsed,
}: {
  item: NavItem;
  collapsed: boolean;
}) {
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        `flex items-center rounded-lg text-[15px] font-medium transition-colors ${
          collapsed ? "justify-center px-0 py-2" : "gap-3 px-3 py-2"
        } ${
          isActive
            ? "bg-primary-50 text-primary-700"
            : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
        }`
      }
    >
      <i className={`${item.icon} text-2xl shrink-0`} aria-hidden="true" />
      {!collapsed && item.label}
    </NavLink>
  );
}

/**
 * TicketsSidebarLink - SidebarLink with the unassigned-tickets-in-the-
 * last-5-days badge. Same UI affordance as the inbox badge: an agent
 * filtered to "Mine" tickets still needs to notice when new
 * unassigned work shows up so they can pick it up. Polls every 30s
 * via useUnassignedTicketCount.
 *
 * The click also carries the clear-search signal (Greg, 2026-07-30):
 * this item means "show me my tickets", so it drops the search term
 * and leaves the filter dropdowns alone - the same list the X in the
 * search box produces. Browser Back does not carry the signal, so
 * returning from a ticket still shows the search results the agent
 * came from. See hooks/useClearSearchSignal.
 */
function TicketsSidebarLink({
  item,
  collapsed,
}: {
  item: NavItem;
  collapsed: boolean;
}) {
  const { data } = useUnassignedTicketCount();
  const navigate = useNavigate();
  const location = useLocation();
  const unassigned = data?.count ?? 0;
  const windowDays = data?.window_days ?? 5;
  const tooltip = collapsed
    ? item.label
    : unassigned > 0
      ? `${unassigned} unassigned in the last ${windowDays} days`
      : undefined;

  function handleClick(e: React.MouseEvent<HTMLAnchorElement>): void {
    // Modified clicks (open in new tab / window) keep the browser
    // default - a fresh tab has no search term to clear anyway.
    if (e.defaultPrevented) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
      return;
    }
    e.preventDefault();
    navigate(item.to, {
      state: clearSearchNavState(),
      // Already on the list: overwrite the entry rather than stacking a
      // duplicate, so Back still steps off the list instead of onto an
      // identical copy of it.
      replace: location.pathname === item.to,
    });
  }

  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      onClick={handleClick}
      title={tooltip}
      className={({ isActive }) =>
        `flex items-center rounded-lg text-[15px] font-medium transition-colors ${
          collapsed ? "justify-center px-0 py-2" : "gap-3 px-3 py-2"
        } ${
          isActive
            ? "bg-primary-50 text-primary-700"
            : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
        }`
      }
    >
      <i className={`${item.icon} text-2xl shrink-0`} aria-hidden="true" />
      {!collapsed && (
        <>
          <span>{item.label}</span>
          {unassigned > 0 && (
            <span
              className="ml-auto rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700"
              data-testid="tickets-unassigned-badge"
            >
              {unassigned}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

/**
 * InboxSidebarLink - identical to SidebarLink but renders a small
 * pending-count badge to the right of the label. Kept separate so
 * non-inbox items don't pay the cost of the count query.
 */
function InboxSidebarLink({
  item,
  collapsed,
}: {
  item: NavItem;
  collapsed: boolean;
}) {
  const { data } = useAdminInboxCount();
  const pending = data?.pending ?? 0;
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        `flex items-center rounded-lg text-[15px] font-medium transition-colors ${
          collapsed ? "justify-center px-0 py-2" : "gap-3 px-3 py-2"
        } ${
          isActive
            ? "bg-primary-50 text-primary-700"
            : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
        }`
      }
    >
      <i className={`${item.icon} text-2xl shrink-0`} aria-hidden="true" />
      {!collapsed && (
        <>
          <span>{item.label}</span>
          {pending > 0 && (
            <span
              className="ml-auto rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700"
              data-testid="inbox-pending-badge"
            >
              {pending}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

// ---------------------------------------------------------------------------
// User Menu Popover
// ---------------------------------------------------------------------------

function UserMenuPopover({
  collapsed,
  onClose,
}: {
  collapsed: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  useEffect(() => {
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const menuItems = [
    {
      label: "Settings",
      icon: "fa-duotone fa-thin fa-gear",
      onClick: () => {
        navigate("/settings");
        onClose();
      },
    },
    ...(user?.isSystemAdmin
      ? [
          {
            label: "System Admin",
            icon: "fa-duotone fa-thin fa-shield-halved",
            onClick: () => {
              navigate("/admin");
              onClose();
            },
          },
        ]
      : []),
    {
      label: "Log out",
      icon: "fa-duotone fa-thin fa-right-from-bracket",
      onClick: () => {
        logout();
        navigate("/login");
      },
      className: "text-red-600 hover:bg-red-50 hover:text-red-700",
    },
  ];

  return (
    <div
      ref={popoverRef}
      className={`absolute z-50 rounded-lg border border-neutral-200 bg-white shadow-lg ${
        collapsed ? "bottom-full left-14 mb-2" : "bottom-full left-4 right-4 mb-2"
      }`}
    >
      {/* User info header */}
      <div className="border-b border-neutral-100 px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700">
            {user?.name?.charAt(0)?.toUpperCase() ?? "U"}
          </div>
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-neutral-900">
              {user?.name ?? "User"}
            </div>
            <div className="truncate text-xs text-neutral-500">
              {user?.email ?? ""}
            </div>
          </div>
        </div>
      </div>

      {/* Menu items */}
      <div className="py-1">
        {menuItems.map((item) => (
          <button
            key={item.label}
            onClick={item.onClick}
            className={`flex w-full items-center gap-3 px-4 py-2 text-sm font-medium transition-colors ${
              item.className ??
              "text-neutral-700 hover:bg-neutral-50 hover:text-neutral-900"
            }`}
          >
            <i className={`${item.icon} text-lg`} aria-hidden="true" />
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AppShell
// ---------------------------------------------------------------------------

/**
 * Application shell that wraps all authenticated pages.
 *
 * Layout mirrors Auware:
 *  - Fixed sidebar on desktop, full (w-64) or icon-rail (w-12)
 *  - Hamburger-toggled drawer on mobile
 *  - Main content area via `<Outlet />`
 */
export default function AppShell() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const user = useAuthStore((s) => s.user);

  // Filter nav items by role.
  const navItems = useMemo(() => {
    return NAV_ITEMS.filter((item) => {
      if (item.adminsOnly && !user?.isSystemAdmin) return false;
      if (item.agentsOnly && !user?.isAgent && !user?.isSystemAdmin)
        return false;
      return true;
    });
  }, [user?.isAgent, user?.isSystemAdmin]);

  useEffect(() => {
    setUserMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex h-full">
      {/* ----------------------------------------------------------------
          Mobile overlay
          ---------------------------------------------------------------- */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* ----------------------------------------------------------------
          Sidebar
          ---------------------------------------------------------------- */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex flex-col border-r border-neutral-200 bg-white transition-all duration-200 ease-in-out lg:static lg:translate-x-0 ${
          sidebarCollapsed ? "w-12" : "w-64"
        } ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        {/* Brand / logo area */}
        <div
          className={`flex h-16 items-center border-b border-neutral-200 ${
            sidebarCollapsed ? "justify-center px-0" : "justify-between px-4"
          }`}
        >
          {sidebarCollapsed ? (
            <NavLink to="/tickets" aria-label="Go to tickets">
              <img
                src="/buyist-logo.png"
                alt="Buyist Support"
                className="h-8 w-8"
              />
            </NavLink>
          ) : (
            <>
              <NavLink
                to="/tickets"
                aria-label="Go to tickets"
                className="flex items-center gap-2"
              >
                <img
                  src="/buyist-logo.png"
                  alt="Buyist Support"
                  className="h-9 w-auto"
                />
                <span className="text-sm font-semibold tracking-tight text-neutral-900">
                  Buyist Support
                </span>
              </NavLink>
              <button
                onClick={() => setSidebarOpen(false)}
                className="rounded-md p-1 text-neutral-400 hover:text-neutral-600 lg:hidden"
                aria-label="Close sidebar"
              >
                <i
                  className="fa-duotone fa-thin fa-xmark text-3xl"
                  aria-hidden="true"
                />
              </button>
            </>
          )}
        </div>

        {/* Navigation links */}
        <nav
          className={`flex-1 space-y-1 overflow-y-auto py-4 ${
            sidebarCollapsed ? "px-1.5" : "px-3"
          }`}
        >
          {navItems.map((item) => {
            if (item.to === "/inbox") {
              return (
                <InboxSidebarLink
                  key={item.to}
                  item={item}
                  collapsed={sidebarCollapsed}
                />
              );
            }
            if (item.to === "/tickets") {
              return (
                <TicketsSidebarLink
                  key={item.to}
                  item={item}
                  collapsed={sidebarCollapsed}
                />
              );
            }
            return (
              <SidebarLink
                key={item.to}
                item={item}
                collapsed={sidebarCollapsed}
              />
            );
          })}
        </nav>

        {/* Collapse / Expand toggle */}
        <div
          className={`border-t border-neutral-200 ${
            sidebarCollapsed ? "px-1.5 py-2" : "px-3 py-2"
          }`}
        >
          <button
            onClick={() => setSidebarCollapsed((c) => !c)}
            className={`flex w-full items-center rounded-lg py-2 text-xs font-medium text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700 ${
              sidebarCollapsed ? "justify-center px-0" : "gap-2 px-3"
            }`}
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed ? (
              <i
                className="fa-duotone fa-thin fa-angles-right text-base"
                aria-hidden="true"
              />
            ) : (
              <>
                <i
                  className="fa-duotone fa-thin fa-angles-left text-base"
                  aria-hidden="true"
                />
                <span>Collapse</span>
              </>
            )}
          </button>
        </div>

        {/* User menu - click to open popover */}
        <div
          className={`relative border-t border-neutral-200 ${
            sidebarCollapsed ? "p-1.5" : "p-3"
          }`}
        >
          {userMenuOpen && (
            <UserMenuPopover
              collapsed={sidebarCollapsed}
              onClose={() => setUserMenuOpen(false)}
            />
          )}

          <button
            onClick={() => setUserMenuOpen((open) => !open)}
            title={sidebarCollapsed ? user?.name ?? "User menu" : undefined}
            className={`flex w-full items-center rounded-lg text-sm font-medium transition-colors hover:bg-neutral-100 ${
              sidebarCollapsed
                ? "justify-center px-0 py-2"
                : "gap-3 px-3 py-2"
            } ${
              userMenuOpen
                ? "bg-neutral-100 text-neutral-900"
                : "text-neutral-600 hover:text-neutral-900"
            }`}
          >
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700">
              {user?.name?.charAt(0)?.toUpperCase() ?? "U"}
            </div>
            {!sidebarCollapsed && (
              <div className="min-w-0 flex-1 text-left">
                <div className="truncate text-sm font-medium text-neutral-900">
                  {user?.name ?? "User"}
                </div>
                <div className="truncate text-xs text-neutral-500">
                  {user?.email ?? ""}
                </div>
              </div>
            )}
            {!sidebarCollapsed && (
              <i
                className={`fa-duotone fa-thin fa-ellipsis text-xl text-neutral-400 transition-transform ${
                  userMenuOpen ? "rotate-90" : ""
                }`}
                aria-hidden="true"
              />
            )}
          </button>
        </div>
      </aside>

      {/* ----------------------------------------------------------------
          Main content area
          ---------------------------------------------------------------- */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {/* Top bar (mobile) */}
        <header className="flex h-16 items-center border-b border-neutral-200 bg-white px-4 lg:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            className="rounded-md p-1 text-neutral-500 hover:text-neutral-700"
            aria-label="Open sidebar"
          >
            <i
              className="fa-duotone fa-thin fa-bars text-4xl"
              aria-hidden="true"
            />
          </button>
          <span className="ml-3 text-lg font-bold text-primary-600">
            Buyist Support
          </span>
        </header>

        {/* Page content - each route renders here. The send-mode
            banner sits at the very top of the scroll container so it
            stays visible while the operator scrolls through ticket
            lists / detail panes. Returns null in production mode
            (the default) so the layout collapses to zero height. */}
        <main className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-neutral-50">
          <EmailSendModeBanner />
          <div className="flex min-h-0 flex-1 flex-col p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
